import type { Color, ContentType } from "@brookbush/database";
import type {
	CreateHighlightInput,
	CreateNoteInput,
	Highlight,
} from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import useSWR, { mutate as globalMutate } from "swr";

import fetcher from "~/utils/fetcher.client";

interface Props {
	contentType?: ContentType;
	contentId?: string;
	highlight?: boolean;
}

const useHighlights = ({ contentId, contentType, highlight }: Props) => {
	const { userId } = useAuth();

	const {
		data: highlights,
		isLoading: isLoadingHighlights,
		mutate,
	} = useSWR<Highlight[]>(
		userId &&
			(highlight && contentId
				? `/annotations/highlights/${contentId}`
				: `/annotations/highlights`),
	);

	const addHighlight = async (
		body: Omit<CreateHighlightInput, "contentType" | "contentId">,
	) => {
		const data = {
			...body,
			contentId,
			contentType,
		};

		await mutate(
			fetcher(`/annotations/highlights`, {
				method: "POST",
				data,
			}),
			{
				revalidate: true,
				populateCache: false,

				optimisticData: highlights?.concat({
					...data,
					id: Math.random() * 1000,
				} as Highlight),
			},
		);

		globalMutate("/annotations/highlights");
	};

	const removeHighlight = async (highlightId: number) => {
		await mutate(
			fetcher(`/annotations/highlights/${highlightId}`, {
				method: "DELETE",
			}),
			{
				revalidate: true,
				populateCache: false,
				optimisticData: highlights?.filter(
					(highlight) => highlight.id !== highlightId,
				),
			},
		);

		globalMutate("/annotations/highlights");
	};

	const updateHighlight = async (
		highlightId: number,
		data: { color?: Color; note?: string },
	) => {
		await mutate(
			fetcher(`/annotations/highlights/${highlightId}`, {
				method: "PATCH",
				data,
			}),
			{
				revalidate: true,
				populateCache: false,
				optimisticData: highlights?.map((highlight) =>
					highlight.id !== highlightId ? highlight : { ...highlight },
				),
			},
		);

		globalMutate("/annotations/highlights");
	};

	const addNoteToHighlight = async (highlightId: number, note: string) => {
		await mutate(
			fetcher(`/annotations/highlights/${highlightId}/notes`, {
				method: "POST",
				data: {
					body: note,
					contentId,
					contentType,
					highlightId,
				} as CreateNoteInput,
			}),
			{
				revalidate: true,
				populateCache: false,
				// optimisticData: highlights?.map((highlight) =>
				//   highlight.id !== highlightId ? highlight : { ...highlight, note }
				// ),
			},
		);
		globalMutate("/annotations/highlights");
	};

	const deleteNoteFromHighlight = async (
		highlightId: number,
		noteId: number,
	) => {
		await mutate(
			fetcher(`/annotations/highlights/${highlightId}/notes/${noteId}`, {
				method: "DELETE",
			}),
			{
				revalidate: true,
				populateCache: false,
				// optimisticData: highlights?.map((highlight) =>
				//   highlight.id !== highlightId
				//     ? highlight
				//     : { ...highlight, note: "" }
				// ),
			},
		);
		globalMutate("/annotations/highlights");
	};

	return {
		highlights,
		isLoadingHighlights,
		addHighlight,
		addNoteToHighlight,
		deleteNoteFromHighlight,
		removeHighlight,
		updateHighlight,
	};
};

export default useHighlights;
